import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import { OMNICHANNEL_CONVERSATIONS_QUERY_KEY } from '@/api/queries/omnichannel/key';
import { getOmnichannelSocketOrigin } from '@/lib/omnichannel/socket-url';
import { getSocketAuthToken } from '@/lib/auth/socket-auth-token';
import { appendMessageToMessagesCache } from '@/lib/omnichannel/append-message-to-cache';
import { applyIncomingMessageToConversationsCache } from '@/lib/omnichannel/apply-incoming-message-to-conversations-cache';
import { refreshLatestOmnichannelMessagesCacheForConversation } from '@/lib/omnichannel/refresh-latest-messages-cache';
import { parseOmnichannelNewMessagePayload } from '@/lib/omnichannel/parse-new-message-payload';

const NEW_MESSAGE_EVENT = 'new_message';

export type OmnichannelSocketHandlers = {
  /** Current conversation id (string); AI drafts apply when WebSocket `conversationId` matches. */
  selectedConversationIdRef: MutableRefObject<string>;
  onAiReplyDraft: (reply: string, conversationId: string) => void;
  onAiClassification?: (
    classification: 'HOT' | 'WARM' | 'COLD' | string | undefined,
    conversationId: string
  ) => void;
  /** Fired when a realtime message arrives for the open thread. */
  onIncomingThreadMessage?: (conversationId: string) => void;
};

/**
 * Subscribes to omnichannel `new_message` over Socket.IO.
 * Refreshes message / conversation queries; applies `ai_response` replies to the composer when the thread is open.
 */
export function useOmnichannelSocket(
  enabled: boolean,
  handlers?: OmnichannelSocketHandlers
) {
  const queryClient = useQueryClient();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return;

    const origin = getOmnichannelSocketOrigin();
    if (!origin) return;

    const token = getSocketAuthToken();

    const socket = io(origin, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      ...(token ? { auth: { token } } : {}),
    });

    let conversationsInvalidateTimer: ReturnType<typeof setTimeout> | null =
      null;

    const scheduleConversationsInvalidate = () => {
      if (conversationsInvalidateTimer) return;
      conversationsInvalidateTimer = setTimeout(() => {
        conversationsInvalidateTimer = null;
        void queryClient.invalidateQueries({
          queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
        });
      }, 400);
    };

    const onNewMessage = (raw: unknown) => {
      const parsed = parseOmnichannelNewMessagePayload(raw);

      if (import.meta.env.DEV) {
        console.groupCollapsed(
          '[Omnichannel WebSocket] new_message',
          new Date().toISOString()
        );
        console.log('raw payload (copy this):', raw);
        console.log('parsed:', parsed);
        if (
          parsed?.kind === 'ai_response' ||
          parsed?.kind === 'lead_classification'
        ) {
          console.log(
            'classification:',
            parsed.kind === 'lead_classification'
              ? parsed.classification
              : (parsed.classification ?? '(none)')
          );
          console.log('conversationId:', parsed.conversationId);
        }
        if (parsed?.kind === 'channel_message' && parsed.classification) {
          console.log(
            'classification (on channel_message):',
            parsed.classification
          );
        }
        if (!parsed) {
          console.warn(
            'Payload could not be parsed — lead status will not update from this event.'
          );
        }
        console.groupEnd();
      }

      if (!parsed) return;

      scheduleConversationsInvalidate();

      const applyClassification = (
        classification: string | undefined,
        conversationId: string
      ) => {
        if (!classification) return;
        handlersRef.current?.onAiClassification?.(
          classification,
          conversationId
        );
      };

      if (parsed.kind === 'channel_message') {
        const conversationId = String(parsed.event.conversationId);

        applyClassification(parsed.classification, conversationId);

        const isOpen =
          conversationId ===
          handlersRef.current?.selectedConversationIdRef.current;

        appendMessageToMessagesCache(queryClient, parsed.event);
        applyIncomingMessageToConversationsCache(queryClient, parsed.event, {
          isOpen,
          classification: parsed.classification,
        });

        if (isOpen) {
          handlersRef.current?.onIncomingThreadMessage?.(conversationId);
        }
      } else if (parsed.kind === 'lead_classification') {
        applyClassification(parsed.classification, parsed.conversationId);
      } else if (parsed.kind === 'ai_response') {
        void refreshLatestOmnichannelMessagesCacheForConversation(
          queryClient,
          parsed.conversationId
        ).catch(() => {
          /* non-blocking */
        });

        if (
          parsed.conversationId ===
          handlersRef.current?.selectedConversationIdRef.current
        ) {
          handlersRef.current?.onIncomingThreadMessage?.(parsed.conversationId);
        }
      }

      if (parsed.kind === 'ai_response') {
        const h = handlersRef.current;
        if (!h) return;

        applyClassification(parsed.classification, parsed.conversationId);

        if (
          parsed.reply &&
          parsed.conversationId === h.selectedConversationIdRef.current
        ) {
          h.onAiReplyDraft(parsed.reply, parsed.conversationId);
        }
      }
    };

    socket.on(NEW_MESSAGE_EVENT, onNewMessage);

    return () => {
      if (conversationsInvalidateTimer) {
        clearTimeout(conversationsInvalidateTimer);
      }
      socket.off(NEW_MESSAGE_EVENT, onNewMessage);
      socket.disconnect();
    };
  }, [enabled, queryClient]);
}
